import { memo, useState } from "react";
import { format } from "date-fns";
import { Ban } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BlacklistEntry } from "@/supabase/blacklist/types/blacklist";

interface BlacklistManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: BlacklistEntry[];
  onUnregister: (nickname: string) => Promise<boolean>;
}

// 거파 명단 다이얼로그 — 목록 + 행별 해제.
// open state는 호출측(BlacklistToggleButton)에 colocate (스펙 §5.3, Home 무접촉).
export const BlacklistManagerDialog = memo(function BlacklistManagerDialog({
  open,
  onOpenChange,
  entries,
  onUnregister,
}: BlacklistManagerDialogProps) {
  const [pendingNickname, setPendingNickname] = useState<string | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Ban className="w-4 h-4 mr-2 text-red-500" />
            거파 명단
          </DialogTitle>
          <DialogDescription>
            등록된 고객이 고객 목록에 다시 나타나면 배지로 표시됩니다.
          </DialogDescription>
        </DialogHeader>
        {entries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">등록된 거파 고객이 없습니다.</p>
        ) : (
          <ul className="max-h-[50vh] overflow-y-auto divide-y">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-center justify-between gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 truncate">{entry.nickname}</p>
                  {entry.reason && (
                    <p className="text-xs text-gray-500 whitespace-pre-wrap break-words">{entry.reason}</p>
                  )}
                  <p className="text-[11px] text-gray-400">
                    {format(new Date(entry.created_at), "yyyy. MM. dd")} 등록
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs flex-shrink-0"
                  disabled={pendingNickname !== null}
                  onClick={async () => {
                    setPendingNickname(entry.nickname);
                    const ok = await onUnregister(entry.nickname);
                    setPendingNickname(null);
                    if (!ok) {
                      toast.error("거파 해제에 실패했습니다. 다시 시도해 주세요.");
                    }
                  }}
                >
                  해제
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
});
